import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "option", "scroller", "fadeLeft", "fadeRight"]

  connect() {
    this.syncFromInput()
    this.boundUpdateFades = this.updateScrollFades.bind(this)
    window.addEventListener("resize", this.boundUpdateFades)
    this.resizeObserver = new ResizeObserver(this.boundUpdateFades)
    if (this.hasScrollerTarget) {
      this.resizeObserver.observe(this.scrollerTarget)
    }
    requestAnimationFrame(() => {
      this.updateScrollFades()
      this.scrollToSelected()
    })
  }

  disconnect() {
    window.removeEventListener("resize", this.boundUpdateFades)
    this.resizeObserver?.disconnect()
  }

  toggle(event) {
    const presetIconName = event.currentTarget.dataset.presetIconName
    if (!presetIconName) return
    if (this.inputTarget.value === presetIconName) {
      this.inputTarget.value = ""
    } else {
      this.inputTarget.value = presetIconName
    }
    this.syncFromInput()
  }

  onScroll() {
    this.updateScrollFades()
  }

  scrollPrevious(event) {
    this.scrollByDirection(event, -1)
  }

  scrollNext(event) {
    this.scrollByDirection(event, 1)
  }

  scrollByDirection(event, direction) {
    event.preventDefault()
    event.stopPropagation()
    if (!this.hasScrollerTarget) return
    const step = Math.max(200, Math.floor(this.scrollerTarget.clientWidth * 0.75))
    this.scrollerTarget.scrollBy({ left: direction * step, behavior: "smooth" })
  }

  scrollToSelected() {
    if (!this.hasScrollerTarget) return
    const selected = this.optionTargets.find(opt => opt.hasAttribute("data-selected"))
    if (!selected) return
    const scroller = this.scrollerTarget
    const optLeft = selected.offsetLeft
    const optWidth = selected.offsetWidth
    const scrollerWidth = scroller.clientWidth
    scroller.scrollLeft = optLeft - (scrollerWidth / 2) + (optWidth / 2)
    this.updateScrollFades()
  }

  syncFromInput() {
    const val = this.inputTarget.value
    this.optionTargets.forEach((opt) => {
      const selected = opt.dataset.presetIconName === val && val !== ""
      if (selected) {
        opt.setAttribute("data-selected", "")
      } else {
        opt.removeAttribute("data-selected")
      }
      opt.setAttribute("aria-pressed", selected ? "true" : "false")
    })
  }

  updateScrollFades() {
    if (!this.hasScrollerTarget) return
    const el = this.scrollerTarget
    const maxScroll = el.scrollWidth - el.clientWidth
    const left = el.scrollLeft
    const epsilon = 2

    const showLeft = maxScroll > epsilon && left > epsilon
    const showRight = maxScroll > epsilon && left < maxScroll - epsilon

    if (this.hasFadeLeftTarget)  this.applyFadeState(this.fadeLeftTarget, showLeft)
    if (this.hasFadeRightTarget) this.applyFadeState(this.fadeRightTarget, showRight)
  }

  applyFadeState(target, show) {
    target.classList.toggle("opacity-0", !show)
    target.classList.toggle("opacity-100", show)
    target.classList.toggle("pointer-events-none", !show)
    target.setAttribute("tabindex", show ? "0" : "-1")
  }
}
